import React, { useState } from "react";
import { toast } from "react-toastify";
import { AppointmentAPI } from "../../services/appointment.service";
import { isBackendUnreachable } from "../../utils/network";

interface Props {
  isOpen: boolean;
  appointmentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const RejectAppointmentModal: React.FC<Props> = ({
  isOpen,
  appointmentId,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleReject = async () => {
    setLoading(true);
    try {
      await AppointmentAPI.reject(appointmentId, reason.trim() || undefined);
      toast.success("Appointment request rejected");
      setReason("");
      onSuccess();
      onClose();
    } catch (e: any) {
      if (isBackendUnreachable(e)) {
        toast.error("You are offline — rejecting appointments requires a connection");
      } else {
        toast.error(e.message || "Failed to reject appointment");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Reject Appointment Request</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-500">
            Are you sure you want to reject this appointment request? The slot will be freed for other patients.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Reason <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Doctor unavailable, schedule conflict..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50"
            >
              Go Back
            </button>
            <button
              onClick={handleReject}
              disabled={loading}
              className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-40"
            >
              {loading ? "Rejecting..." : "Reject Appointment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
